/**
 * Helper functions to update debug overlay from anywhere in the experiment
 * These functions safely check if debug overlay exists before updating
 */

/**
 * Updates the login information in the debug overlay
 * @param {JsPsych} jsPsych - The jsPsych instance
 * @param {string} studyID - Study ID
 * @param {string} participantID - Participant ID
 * @param {string} sessionID - Session ID
 */
export function updateDebugLoginInfo(jsPsych, studyID, participantID, sessionID) {
  if (jsPsych?.debugOverlay) {
    jsPsych.debugOverlay.setLoginInfo(studyID, participantID, sessionID);
  }
}

/**
 * Updates metadata source in the debug overlay
 * @param {JsPsych} jsPsych - The jsPsych instance
 * @param {string} metadataSource - Metadata source label (localmetadata/firebase)
 */
export function updateDebugMetadataSource(jsPsych, metadataSource) {
  if (jsPsych?.debugOverlay) {
    jsPsych.debugOverlay.setMetadataSource(metadataSource);
  }
}

/**
 * Updates the mission configuration in the debug overlay
 * @param {JsPsych} jsPsych - The jsPsych instance
 * @param {Object} config - Mission config object containing:
 *   - mission: Mission number
 *   - build_date_time: Build date/time string
 *   - instructionsMaxRetries: Max retries
 *   - missionLives: Mission lives
 *   - missionMaxCrash: Max crashes
 *   - missionMinScore: Min score
 *   - missionNumTrials: Number of trials
 *   - thisFile: File URL
 *   - missionIsAdaptive: Whether this mission requests more trials from the backend once its static trials run out (aka "RLexperiment")
 */
export function updateDebugMissionConfig(jsPsych, config) {
  if (jsPsych?.debugOverlay) {
    jsPsych.debugOverlay.setMissionConfig(config);
  }
}

/**
 * Updates the trial information in the debug overlay
 * @param {DebugOverlay} debugOverlay - The DebugOverlay instance
 * @param {number|string} trialNumber - Current trial number
 * @param {string} trialType - Type of trial (TASK, INSTRUCTION, etc.)
 * @param {string} [correctKeycode] - Correct keycode for this trial (optional)
 * @param {string} [correctKeychar] - Correct key character for this trial (optional)
 */
export function updateDebugTrial(debugOverlay, trialNumber, trialType = null, correctKeycode = null, correctKeychar = null) {
  if (debugOverlay && typeof debugOverlay.setTrial === 'function') {
    debugOverlay.setTrial(trialNumber, trialType, correctKeycode, correctKeychar);
  }
}

/**
 * Resets the debug overlay to default state
 * @param {JsPsych} jsPsych - The jsPsych instance
 */
export function resetDebugOverlay(jsPsych) {
  if (jsPsych?.debugOverlay) {
    jsPsych.debugOverlay.reset();
  }
}

/**
 * Updates the keyboard layout in the debug overlay
 * @param {DebugOverlay} debugOverlay - The DebugOverlay instance
 * @param {string} layoutId - Layout ID (e.g., "QWERTY_USUK", "AZERTY_FR")
 */
export function updateDebugKeyboardLayout(debugOverlay, layoutId) {
  if (debugOverlay && typeof debugOverlay.setKeyboardLayout === 'function') {
    debugOverlay.setKeyboardLayout(layoutId);
  }
}
